import React, { useEffect, useMemo, useState } from 'react';
import Spinner from './Spinner';
import { fmtDate } from './format';
import { getFortnoxOrderNumbers, getOrderNumbers } from './salesCalculations';
import { post } from '../../../Services/http';
import { toError } from './useSalesInvoices';
import {
  cardStyle,
  headRowStyle,
  headTextStyle,
  linkStyle,
  pageHeaderStyle,
  stripeStyle,
  tableStyle,
  textCellStyle,
} from './styles';

// The Freshdesk ticket statuses
const STATUSES = { 2: 'Öppen', 3: 'Väntar', 4: 'Löst', 5: 'Stängd' };
const FOUND_BY = {
  CustomerEmail: 'Kundens e-post',
  OrderNumber: 'Ordernummer',
};

// Why an article is returned, shown inline below its row in the returned articles table.
// article: { articleNumber, name, invoices } where invoices are the credit invoices of the article.
// invoicesByNumber: Map of document number to invoice, to find the invoice that a credit invoice credits.
const ReturnAnalysis = ({ article, invoicesByNumber }) => {
  // The e-mail addresses are on the Fortnox orders, the order numbers on the invoices are used as well when they are there
  const fortnoxOrderNumbers = useMemo(
    () => getFortnoxOrderNumbers(article.invoices, invoicesByNumber),
    [article, invoicesByNumber]
  );
  const orderNumbers = useMemo(
    () => getOrderNumbers(article.invoices, invoicesByNumber),
    [article, invoicesByNumber]
  );
  const invoicesWithoutOrder = useMemo(
    () =>
      article.invoices.filter(
        (invoice) =>
          getFortnoxOrderNumbers([invoice], invoicesByNumber).length === 0
      ).length,
    [article, invoicesByNumber]
  );
  const hasOrders = fortnoxOrderNumbers.length > 0 || orderNumbers.length > 0;
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  // The tickets given to the model are the most recently updated ones, the ones that support a reason first
  const analyzedTickets = useMemo(() => {
    if (!result) return [];

    const reasonsByTicket = new Map();
    result.reasons.forEach((reason) =>
      reason.ticketIds.forEach((id) =>
        reasonsByTicket.set(id, [
          ...(reasonsByTicket.get(id) || []),
          reason.reason,
        ])
      )
    );

    return result.tickets
      .slice(0, result.analyzedTicketCount)
      .map((ticket) => ({
        ...ticket,
        reasons: reasonsByTicket.get(ticket.id) || [],
      }))
      .sort((a, b) => (b.reasons.length > 0) - (a.reasons.length > 0));
  }, [result]);

  useEffect(() => {
    if (!hasOrders) return undefined;

    let cancelled = false;
    setResult(null);
    setError(null);

    post('/api/return-analysis', {
      articleNumber: article.articleNumber,
      articleName: article.name,
      fortnoxOrderNumbers,
      orderNumbers,
    })
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled) setResult(data);
      })
      .catch(async (err) => {
        const failure = await toError(err);
        if (!cancelled) setError(failure.message);
      });

    return () => {
      cancelled = true;
    };
  }, [article, fortnoxOrderNumbers, orderNumbers, hasOrders, attempt]);

  return (
    <section aria-label={`Returorsak ${article.name}`}>
      <div style={pageHeaderStyle}>
        <h4 style={{ margin: '0 0 0.25rem', fontSize: '0.95rem' }}>
          Returorsak: {article.name} ({article.articleNumber})
        </h4>
        <div style={{ color: '#666', fontSize: '0.85rem' }}>
          {fortnoxOrderNumbers.length} ordrar på {article.invoices.length}{' '}
          kreditfakturor
          {invoicesWithoutOrder > 0 &&
            ` (${invoicesWithoutOrder} kreditfakturor saknar order)`}
          {result && `, ${result.customersFound} kunder hittades`}
          {result &&
            result.ordersTotal > result.ordersAnalyzed &&
            `. Bara de ${result.ordersAnalyzed} senaste ordrarna analyseras.`}
        </div>
      </div>

      {!hasOrders && (
        <div role="alert" style={{ color: '#cc4b37' }}>
          Ingen order eller inget ordernummer (LS…) hittades på kreditfakturorna
          för artikeln, så det finns inga ärenden att söka efter.
        </div>
      )}
      {hasOrders && !result && !error && (
        <div role="status" style={{ padding: '1rem 0' }}>
          <Spinner />
          Hämtar ärenden från Freshdesk och analyserar dem, det kan ta en stund
        </div>
      )}
      {error && (
        <div role="alert" style={{ color: '#cc4b37' }}>
          Kunde inte analysera returorsaken: {error}
        </div>
      )}

      {result && (
        <>
          <div role="status">
            <p style={{ margin: '0 0 0.75rem', whiteSpace: 'pre-wrap' }}>
              {result.reasonText}
            </p>
          </div>

          {analyzedTickets.length > 0 && (
            <details>
              <summary style={{ ...linkStyle, width: 'fit-content' }}>
                Visa ärenden som analysen bygger på ({analyzedTickets.length}
                {result.tickets.length > analyzedTickets.length &&
                  ` av ${result.tickets.length}`}
                )
              </summary>
              <div style={{ ...cardStyle, marginTop: '0.75rem' }}>
                <table style={tableStyle}>
                  <thead>
                    <tr style={headRowStyle}>
                      <th style={headTextStyle}>Ärende</th>
                      <th style={headTextStyle}>Stöder orsak</th>
                      <th style={headTextStyle}>Status</th>
                      <th style={headTextStyle}>Uppdaterad</th>
                      <th style={headTextStyle}>Hittad via</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analyzedTickets.map((ticket, i) => (
                      <tr key={ticket.id} style={stripeStyle(i)}>
                        <td style={textCellStyle}>
                          <a
                            href={ticket.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={linkStyle}
                          >
                            #{ticket.id} {ticket.subject}
                          </a>
                        </td>
                        <td style={textCellStyle}>
                          {ticket.reasons.length > 0
                            ? ticket.reasons.join(', ')
                            : '–'}
                        </td>
                        <td style={textCellStyle}>
                          {STATUSES[ticket.status] || ticket.status}
                        </td>
                        <td style={textCellStyle}>
                          {fmtDate(ticket.updatedAt)}
                        </td>
                        <td style={textCellStyle}>
                          {ticket.foundBy
                            .map((match) => FOUND_BY[match] || match)
                            .join(', ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </>
      )}
      {(error ||
        result?.status === 'Failed' ||
        result?.status === 'NotConfigured') && (
        <button
          type="button"
          onClick={() => setAttempt((value) => value + 1)}
          style={{ ...linkStyle, border: 0, background: 'none', padding: 0 }}
        >
          Försök igen
        </button>
      )}
    </section>
  );
};

export default ReturnAnalysis;
